import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ExpenseEditPage.css';
import { getExpenseTypes, createExpense, updateExpense } from '../../api/expenses'; // FIX #2: import updateExpense
import { useSelector } from 'react-redux';
import { useExpenseContext } from './context/ExpenseContext';
import { mapSavedExpenseToUi } from './mapSavedExpenseToUi';
import { useTranslation } from 'react-i18next';
 
const statusColors = {
  draft: '#bdbdbd',
  pending: '#1976d2',
  approved: '#388e3c',
  rejected: '#d32f2f',
};
 
const STATUS_MAP = {
  1: 'draft',
  2: 'pending',
  3: 'approved',
  4: 'rejected',
  draft: 'draft',
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
};
 
const ExpenseEditPage = ({ mode }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const reduxUser = useSelector((state) => state.auth.user);

  useEffect(() => {
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);
 
  const {
    selectedExpense,
    updateExpenseInCache,
    addExpenseToCache
  } = useExpenseContext();
 
  const passedExpenseFromState = location.state?.expense || null;
  const passedExpense = passedExpenseFromState || selectedExpense || null;
  const from = location.state?.from || null;
  const to = location.state?.to || null;
 
  const [form, setForm] = useState({
    project: '',
    expenseTypeId: '',
    amount: '',
    date: null,
    description: '',
    status: 'draft',
    _expenseTypeFallbackName: '',
    _expenseTypeFallbackCode: '',
  });
 
  const [isEditingDisabled, setIsEditingDisabled] = useState(false);
 
  const [header, setHeader] = useState({
    orgName: '',
    costCenter: '',
    deptName: '',
    userName: '',
    approverName: '',
  });
 
  const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
  const [expenseTypesError, setExpenseTypesError] = useState('');
  const [errors, setErrors] = useState({});
 
  const reduxProjects = reduxUser?.projects || [];
  const projectsToUse = reduxProjects;
 
  const id =
    paramId ||
    passedExpense?.expenseId ||
    passedExpense?.id ||
    '';
 
  function normalizeExpenseType(raw) {
    if (!raw) return null;
 
    const idVal =
      raw?.expenseTypeId ??
      raw?.expense_type_id ??
      raw?.expTypeId ??
      raw?.id ??
      null;
 
    const codeVal =
      raw?.expenseTypeCode ??
      raw?.expense_type_code ??
      raw?.typeCode ??
      raw?.code ??
      null;
 
    const nameVal =
      raw?.expenseTypeName ??
      raw?.expTypeName ??
      raw?.typeName ??
      raw?.name ??
      null;
 
    if (!idVal && !codeVal && !nameVal) return null;
 
    return {
      id: idVal != null ? String(idVal) : '',
      code: codeVal != null ? String(codeVal) : '',
      name: nameVal != null ? String(nameVal) : '',
      raw,
    };
  }
 
  function uniqueByIdOrCode(list) {
    const seen = new Set();
    const out = [];
 
    for (const item of list) {
      if (!item) continue;
      const key = item.id || item.code || item.name;
      if (!key) continue;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
 
    return out;
  }
 
  function getAuthUser() {
    return reduxUser ?? null;
  }
 
  function parseLocalDate(dateValue) {
    if (!dateValue) return null;
    if (dateValue instanceof Date) return dateValue;
 
    if (typeof dateValue === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        const [year, month, day] = dateValue.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      return new Date(dateValue);
    }
 
    return null;
  }
 
  function formatLocalDate(dateValue) {
    if (!dateValue) return '';
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
 
  useEffect(() => {
    (async () => {
      try {
        setExpenseTypesError('');
        const types = await getExpenseTypes({ orgId: 1 });
        const normalized = uniqueByIdOrCode((types || []).map(normalizeExpenseType));
        setExpenseTypeOptions(normalized);
      } catch (e) {
        console.error('Failed to load expense types', e);
        setExpenseTypeOptions([]);
 
        const status = e?.response?.status;
        if (status === 403) {
          setExpenseTypesError(t('expenseEdit.sessionExpired'));
        } else if (status === 500) {
          setExpenseTypesError(t('expenseEdit.expenseTypesUnavailable'));
        } else {
          setExpenseTypesError(t('expenseEdit.expenseTypesLoadFailed'));
        }
      }
    })();
  }, [t, i18n.language]);
 
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewOnly = params.get('viewOnly') === 'true';
 
    if ((mode === 'edit' || mode === 'view') && passedExpense) {
      const normalizedExpenseType = normalizeExpenseType(
        passedExpense.expenseTypeObj ||
        passedExpense.expenseType ||
        passedExpense._raw?.expenseType
      );
 
      const rawStatus = passedExpense.status;
      const mappedStatus = STATUS_MAP[rawStatus] || 'draft';
 
      setHeader({
        orgName:
          passedExpense.organization?.orgName ||
          passedExpense._raw?.organization?.orgName ||
          '-',
        costCenter:
          passedExpense.department?.costCenter?.costCenterName ||
          passedExpense.department?.costCenter?.ccName ||
          passedExpense._raw?.department?.costCenter?.costCenterName ||
          passedExpense._raw?.department?.costCenter?.ccName ||
          passedExpense.costCenterName ||
          '-',
        deptName:
          passedExpense.department?.deptName ||
          passedExpense._raw?.department?.deptName ||
          '-',
        userName:
          `${passedExpense.user?.firstName || passedExpense._raw?.user?.firstName || ''} ${passedExpense.user?.lastName || passedExpense._raw?.user?.lastName || ''}`.trim() || '-',
        approverName:
          `${passedExpense.approver?.firstName || passedExpense._raw?.approver?.firstName || ''} ${passedExpense.approver?.lastName || passedExpense._raw?.approver?.lastName || ''}`.trim() || '-',
      });
 
      setForm({
        project:
          passedExpense.projectObj?.projectCode ||
          passedExpense._raw?.project?.projectCode ||
          passedExpense.projectName ||
          passedExpense.project ||
          '',
        expenseTypeId: normalizedExpenseType?.id || '',
        amount:
          passedExpense.expenseAmount ??
          passedExpense._raw?.expenseAmount ??
          passedExpense.amount ??
          '',
        date:
          parseLocalDate(passedExpense.expenseDate) ||
          parseLocalDate(passedExpense._raw?.expenseDate) ||
          parseLocalDate(passedExpense.date),
        description:
          passedExpense.description ??
          passedExpense._raw?.description ??
          '',
        status: mappedStatus,
        _expenseTypeFallbackName:
          passedExpense.expenseTypeObj?.expenseTypeName ||
          passedExpense._raw?.expenseType?.expenseTypeName ||
          passedExpense.expenseTypeName ||
          passedExpense.expenseType ||
          '',
        _expenseTypeFallbackCode: normalizedExpenseType?.code || '',
      });
 
      if (viewOnly) {
        setIsEditingDisabled(true);
      } else {
        setIsEditingDisabled(!(mappedStatus === 'draft' || mappedStatus === 'rejected'));
      }
 
      return;
    }
 
    if (mode === 'add' && reduxUser) {
      setHeader({
        orgName: reduxUser.department?.costCenter?.organization?.orgName || '-',
        costCenter:
          reduxUser.department?.costCenter?.costCenterName ||
          reduxUser.department?.costCenter?.ccName ||
          '-',
        deptName: reduxUser.department?.deptName || '-',
        userName: `${reduxUser.firstName || ''} ${reduxUser.lastName || ''}`.trim() || '-',
        approverName:
          `${reduxUser.approver?.firstName || ''} ${reduxUser.approver?.lastName || ''}`.trim() || '-',
      });
      setIsEditingDisabled(false);
    }
  }, [mode, location.search, passedExpense, reduxUser]);
 
  useEffect(() => {
    if (!expenseTypeOptions.length) return;
 
    setForm((prev) => {
      if (prev.expenseTypeId) return prev;
 
      const fallbackName = prev._expenseTypeFallbackName || '';
      const fallbackCode = prev._expenseTypeFallbackCode || '';
 
      const match =
        expenseTypeOptions.find((opt) => opt.code && fallbackCode && opt.code === fallbackCode) ||
        expenseTypeOptions.find((opt) => opt.name && fallbackName && opt.name === fallbackName) ||
        expenseTypeOptions.find((opt) => opt.code && fallbackName && opt.code === fallbackName);
 
      return {
        ...prev,
        expenseTypeId: match?.id || '',
      };
    });
  }, [expenseTypeOptions]);
 
  const handleChange = (e) => {
    const { name, value } = e.target;
 
    if (name === 'amount') {
      const cleaned = value.replace(/[^\d.]/g, '');
      const parts = cleaned.split('.');
      const formatted =
        parts.length > 1
          ? `${parts[0]}.${parts[1].slice(0, 2)}`
          : parts[0];
 
      setForm((prev) => ({ ...prev, amount: formatted }));
      return;
    }
 
    if (name === 'description') {
      setForm((prev) => ({ ...prev, description: value.slice(0, 512) }));
      return;
    }
 
    setForm((prev) => ({ ...prev, [name]: value }));
  };
 
  const handleDateChange = (date) => {
    setForm((prev) => ({ ...prev, date }));
  };
 
  function validateRequired(f) {
    const e = {};
 
    if (!String(f.project || '').trim()) e.project = t('expenseEdit.validation.projectRequired');
    if (!String(f.expenseTypeId || '').trim()) e.expenseType = t('expenseEdit.validation.expenseTypeRequired');
    if (!String(f.amount || '').trim()) e.amount = t('expenseEdit.validation.amountRequired');
    if (Number(f.amount) <= 0) e.amount = t('expenseEdit.validation.amountMin');
    if (!f.date) e.date = t('expenseEdit.validation.dateRequired');
 
    return e;
  }
 
  function buildExpensePayload(statusValue) {
    const authUser = getAuthUser();
 
    const userId =
      authUser?.userId ??
      passedExpense?._raw?.user?.userId ??
      passedExpense?.user?.userId ??
      null;
 
    const approverId =
      authUser?.approver?.userId ??
      passedExpense?._raw?.approver?.userId ??
      passedExpense?.approver?.userId ??
      null;
 
    const deptId =
      authUser?.department?.deptId ??
      passedExpense?._raw?.department?.deptId ??
      passedExpense?.department?.deptId ??
      null;
 
    const orgId =
      authUser?.department?.costCenter?.organization?.orgId ??
      passedExpense?._raw?.organization?.orgId ??
      passedExpense?.organization?.orgId ??
      null;
 
    const selectedProject = projectsToUse.find(
      (p) => String(p?.projectCode ?? '') === String(form.project)
    );
 
    const projectId =
      selectedProject?.projectId ??
      passedExpense?._raw?.project?.projectId ??
      passedExpense?.project?.projectId ??
      null;
 
    const expenseTypeId = form.expenseTypeId ? Number(form.expenseTypeId) : null;
    const formattedDate = formatLocalDate(form.date);
 
    return {
      ...(passedExpense
        ? {
            expenseId:
              passedExpense.expenseId ??
              passedExpense.id ??
              null,
          }
        : {}),
      user: { userId },
      approver: { userId: approverId },
      department: { deptId },
      organization: { orgId },
      project: { projectId },
      expenseType: { expenseTypeId },
      expenseAmount: Number(form.amount),
      expenseDate: formattedDate,
      status: statusValue,
      description: form.description || '',
      createUid: userId,
      updateUid: userId,
    };
  }
 
  const submitExpense = async (statusValue) => {
    const validationErrors = validateRequired(form);
    setErrors(validationErrors);
 
    if (Object.keys(validationErrors).length > 0) return;
 
    try {
      const payload = buildExpensePayload(statusValue);
 
      // FIX #2: use updateExpense for edit mode, createExpense for add mode
      const isEdit = mode === 'edit' || !!passedExpense;
      const response = isEdit
  ? await updateExpense(payload)
  : await createExpense(payload);

console.log('API response:', response); // temporary debug
console.log('API response.data:', response?.data); // temporary debug

const savedExpense = response?.data ?? response;
if (!savedExpense) throw new Error('No data returned from server');
console.log('savedExpense:', savedExpense);

      const uiExpense = mapSavedExpenseToUi(
        savedExpense,
        statusValue,
        form,
        projectsToUse,
        expenseTypeOptions
      );
 
      if (isEdit) {
        updateExpenseInCache(uiExpense);
      } else {
        addExpenseToCache(uiExpense);
      }
 
      navigate('/expenses', { state: { from, to } });
    } catch (err) {
      console.error(err);
      setErrors({
        api: statusValue === 1
          ? t('expenseEdit.saveFailed')
          : t('expenseEdit.submitFailed'),
      });
    }
  };
 
  const handleSave = async () => {
    await submitExpense(1);
  };
 
  const handleSubmit = async () => {
    await submitExpense(2);
  };
 
  const handleCancel = () => {
    navigate('/expenses', { state: { from, to } });
  };
 
  const statusLabel = STATUS_MAP[form.status] || 'draft';
 
  return (
    <div className="exp-edit-container">
      <h2 className="exp-edit-title">{t('expenseEdit.title')}</h2>

      <div className="exp-edit-status-row">
        <div
          className="exp-edit-status"
          style={{
            background: statusColors[statusLabel] || '#bdbdbd',
          }}
        >
          {t('expenseEdit.status')}: {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
        </div>
      </div>

      <div className="exp-edit-meta-stack">
        {id && (
          <div className="exp-edit-meta-line">
            {t('expenseEdit.id')}: {id}
          </div>
        )}
        <div className="exp-edit-meta-line">
          {t('expenseEdit.orgName')}: {header.orgName || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('expenseEdit.costCenter')}: {header.costCenter || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('expenseEdit.deptName')}: {header.deptName || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('expenseEdit.user')}: {header.userName || '-'}
        </div>
        <div className="exp-edit-meta-line">
          {t('expenseEdit.approver')}: {header.approverName || '-'}
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="exp-edit-form-section">
          <div className="exp-edit-form-group">
            <label>{t('expenseEdit.project')}:</label>
            <select
              name="project"
              value={form.project}
              onChange={handleChange}
              disabled={isEditingDisabled}
              className="exp-edit-input"
            >
              <option value="">{t('expenseEdit.selectProject')}</option>
              {projectsToUse.map((p) => {
                const value = p?.projectCode ?? String(p?.projectId ?? '');
                const label =
                  p?.projectName ||
                  (p?.projectCode
                    ? `${p.projectCode}${p.description ? ` - ${p.description}` : ''}`
                    : p?.description ?? value);

                return (
                  <option key={p?.projectId ?? value} value={value}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="exp-edit-form-group">
            <label>{t('expenseEdit.expenseType')}:</label>
            <select
              name="expenseTypeId"
              value={form.expenseTypeId}
              onChange={handleChange}
              disabled={isEditingDisabled}
              className="exp-edit-input"
            >
              <option value="">{t('expenseEdit.selectExpenseType')}</option>
              {expenseTypeOptions.map((opt) => {
                const optionId = String(opt?.id ?? '');
                const code = opt?.code ?? '';
                const name = opt?.name ?? '';
                const label = name || code || optionId;

                return (
                  <option key={optionId || label} value={optionId}>
                    {label}
                  </option>
                );
              })}
            </select>

            {expenseTypesError && (
              <div style={{ color: 'red', marginTop: 6, fontSize: 13 }}>
                {expenseTypesError}
              </div>
            )}
          </div>

          <div className="exp-edit-form-group">
            <label>{t('expenseEdit.amount')} ($):</label>
            <input
              type="text"
              name="amount"
              value={form.amount}
              onChange={handleChange}
              disabled={isEditingDisabled}
              className="exp-edit-input"
              min="0"
              step="0.01"
              placeholder="0.00"
              pattern="^\\d+(\\.\\d{1,2})?$"
              maxLength={12}
            />
          </div>

          <div className="exp-edit-form-group">
            <label>{t('expenseEdit.date')}:</label>
            <DatePicker
              selected={form.date}
              onChange={handleDateChange}
              disabled={isEditingDisabled}
              className="exp-edit-input"
              wrapperClassName="exp-edit-datepicker-wrapper"
              placeholderText={t('expenseEdit.selectDate')}
              dateFormat="yyyy-MM-dd"
            />
          </div>

          <div className="exp-edit-form-group">
            <label>
              {t('expenseEdit.description')}:{' '}
              <span style={{ fontWeight: 500, color: '#888', fontSize: 13 }}>
                ({t('expenseEdit.maxChars')})
              </span>
            </label>

            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              disabled={isEditingDisabled}
              className="exp-edit-input exp-edit-textarea"
              placeholder={t('expenseEdit.descriptionPlaceholder')}
              rows="3"
              maxLength={512}
            />

            {(errors.project || errors.expenseType || errors.amount || errors.date) && (
              <div style={{ color: 'red', marginTop: 6, fontSize: 13 }}>
                {errors.project && <div>{errors.project}</div>}
                {errors.expenseType && <div>{errors.expenseType}</div>}
                {errors.amount && <div>{errors.amount}</div>}
                {errors.date && <div>{errors.date}</div>}
              </div>
            )}

            {errors.api && (
              <div style={{ color: 'red', marginTop: 6, fontSize: 13 }}>
                {errors.api}
              </div>
            )}
          </div>
        </div>

        <div className="exp-edit-btn-row">
          {!isEditingDisabled && (
            <>
              <button type="button" onClick={handleSave} className="exp-edit-btn save">
                {t('expenseEdit.save')}
              </button>
              <button type="button" onClick={handleSubmit} className="exp-edit-btn submit">
                {t('expenseEdit.submit')}
              </button>
            </>
          )}
          <button type="button" onClick={handleCancel} className="exp-edit-btn cancel">
            {t('expenseEdit.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
};
 
export default ExpenseEditPage;